import type { ReportConfigurationType } from '@api';
import { ActionsCell, ReportVariantUnionType } from '@common';
import { DataTableColumn } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';
import { ActiveTableCell, LabelTableCell } from './components';

type UseReportMailingsTableColumnsParamsType = {
  variant: ReportVariantUnionType;
  canEdit: boolean;
  onEdit: (mailing: ReportConfigurationType) => void;
  onDelete: (mailingId: string) => void;
};

export type UseReportMailingsTableColumnsReturnType = {
  columns: DataTableColumn<ReportConfigurationType>[];
};

export const useReportMailingsTableColumns = ({
  variant,
  canEdit,
  onEdit,
  onDelete,
}: UseReportMailingsTableColumnsParamsType): UseReportMailingsTableColumnsReturnType => {
  const { t } = useTranslation();
  const noDataPlaceholder = t('NO_DATA_PLACEHOLDER');
  const isPerson = variant === 'PERSON';

  const columns: DataTableColumn<ReportConfigurationType>[] = [
    {
      accessor: 'label',
      title: t('REPORTS.MAILINGS.TABLE_COLUMNS.LABEL'),
      render: ({ label, internalNote }) => (
        <LabelTableCell label={label} internalNote={internalNote} />
      ),
    },
    {
      accessor: 'isEnabled',
      title: t('REPORTS.MAILINGS.TABLE_COLUMNS.ACTIVE'),
      render: ({ isEnabled }) => <ActiveTableCell isEnabled={isEnabled} />,
    },
    {
      accessor: 'sendWeekday',
      title: t('REPORTS.MAILINGS.TABLE_COLUMNS.SEND_DAY'),
      render: ({ sendWeekday }) =>
        sendWeekday
          ? t(`REPORTS.ENUMS.WEEKDAY.${sendWeekday}`)
          : noDataPlaceholder,
    },
    {
      accessor: 'sendTime',
      title: t('REPORTS.MAILINGS.TABLE_COLUMNS.SEND_TIME'),
    },
    {
      accessor: 'locale',
      title: t('REPORTS.MAILINGS.TABLE_COLUMNS.LANGUAGE'),
      render: ({ locale }) => t(`REPORTS.ENUMS.LOCALE.${locale}`),
    },
    {
      accessor: 'scope',
      hidden: !isPerson,
      title: t('REPORTS.MAILINGS.TABLE_COLUMNS.SCOPE'),
      render: ({ configurationOptions }) =>
        configurationOptions.kind === 'PERSON'
          ? t(`REPORTS.ENUMS.SCOPE.${configurationOptions.scope}`)
          : noDataPlaceholder,
    },
    {
      accessor: 'revenue',
      hidden: !isPerson,
      title: t('REPORTS.MAILINGS.TABLE_COLUMNS.REVENUE'),
      render: ({ configurationOptions }) =>
        configurationOptions.kind === 'PERSON'
          ? t(
              `REPORTS.ENUMS.REVENUE.${configurationOptions.includeRevenue ? 'WITH' : 'WITHOUT'}`
            )
          : noDataPlaceholder,
    },
    {
      accessor: 'reportType',
      title: t('REPORTS.MAILINGS.TABLE_COLUMNS.REPORT'),
      render: ({ reportType }) => t(`REPORTS.ENUMS.REPORT_TYPE.${reportType}`),
    },
    {
      accessor: 'recipient',
      hidden: !isPerson,
      title: t('REPORTS.MAILINGS.TABLE_COLUMNS.RECIPIENT'),
      render: ({ configurationOptions }) =>
        configurationOptions.kind === 'PERSON'
          ? (configurationOptions.recipientMail ?? noDataPlaceholder)
          : noDataPlaceholder,
    },
    {
      accessor: 'recipientGroup',
      hidden: isPerson,
      title: t('REPORTS.MAILINGS.TABLE_COLUMNS.RECIPIENT_GROUP'),
      render: ({ configurationOptions }) =>
        configurationOptions.kind === 'VENUE'
          ? (configurationOptions.recipientGroup?.label ?? noDataPlaceholder)
          : noDataPlaceholder,
    },
    {
      accessor: 'actions',
      title: t('REPORTS.MAILINGS.TABLE_COLUMNS.ACTIONS'),
      titleStyle: { textAlign: 'center' },
      width: 100,
      hidden: !canEdit,
      render: (record) => (
        <ActionsCell.Root>
          <ActionsCell.Edit onClick={() => onEdit(record)} />
          <ActionsCell.Delete onClick={() => onDelete(record.id)} />
        </ActionsCell.Root>
      ),
    },
  ];

  return { columns };
};
